import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useThrottledResize } from '../hooks/useThrottledResize';

export interface TooltipProps {
  children: React.ReactElement;
  content: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  /** When false, tooltip only shows on hover (not on focus). Use for triggers that open a menu on click to avoid showing both tooltip and menu. Default true. */
  showOnFocus?: boolean;
}

interface Position {
  top: number;
  left: number;
}

export const Tooltip: React.FC<TooltipProps> = ({ 
  children, 
  content, 
  position = 'top',
  delay = 100,
  showOnFocus = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<Position>({ top: -9999, left: -9999 });
  const [actualPosition, setActualPosition] = useState<'top' | 'bottom' | 'left' | 'right'>(position);
  const [isPositioned, setIsPositioned] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isHoveringRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  // Check if screen is lg or larger (1024px+)
  // Hover tooltips are disabled on md (768px) and below for better mobile/tablet UX
  const checkScreenSize = useCallback(() => {
    setIsLargeScreen(window.innerWidth >= 1024);
  }, []);
  
  useEffect(() => {
    // Check on mount
    checkScreenSize();
  }, [checkScreenSize]);
  
  // Use throttled resize to prevent lag
  useThrottledResize(checkScreenSize);

  const clearShowTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const clearRaf = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const calculatePosition = useCallback((): Position | null => {
    if (!triggerRef.current || !tooltipRef.current) return null;
    
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    
    const padding = 8;
    const spacing = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let top = 0;
    let left = 0;
    let finalPosition = position;
    
    // Calculate initial position based on preferred position
    switch (position) {
      case 'top':
        top = triggerRect.top + scrollY - tooltipRect.height - spacing;
        left = triggerRect.left + scrollX + (triggerRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'bottom':
        top = triggerRect.bottom + scrollY + spacing;
        left = triggerRect.left + scrollX + (triggerRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'left':
        top = triggerRect.top + scrollY + (triggerRect.height / 2) - (tooltipRect.height / 2);
        left = triggerRect.left + scrollX - tooltipRect.width - spacing;
        break;
      case 'right':
        top = triggerRect.top + scrollY + (triggerRect.height / 2) - (tooltipRect.height / 2);
        left = triggerRect.right + scrollX + spacing;
        break;
    }
    
    // Check if tooltip fits in viewport, adjust position if needed
    const fitsHorizontally = left >= padding && left + tooltipRect.width <= viewportWidth - padding;
    const fitsVertically = top >= scrollY + padding && top + tooltipRect.height <= scrollY + viewportHeight - padding;
    
    // Adjust horizontal position if needed
    if (!fitsHorizontally) {
      if (left < padding) {
        left = padding;
      } else if (left + tooltipRect.width > viewportWidth - padding) {
        left = viewportWidth - tooltipRect.width - padding;
      }
    }
    
    // Adjust vertical position if needed, flip to opposite side if it doesn't fit
    if (!fitsVertically) {
      if (top < scrollY + padding) {
        // Tooltip would go above viewport
        if (position === 'top') {
          // Try bottom instead
          top = triggerRect.bottom + scrollY + spacing;
          finalPosition = 'bottom';
        } else {
          top = scrollY + padding;
        }
      } else if (top + tooltipRect.height > scrollY + viewportHeight - padding) {
        // Tooltip would go below viewport
        if (position === 'bottom') {
          // Try top instead
          top = triggerRect.top + scrollY - tooltipRect.height - spacing;
          finalPosition = 'top';
        } else {
          top = scrollY + viewportHeight - tooltipRect.height - padding;
        }
      }
    }
    
    // Recalculate horizontal centering after vertical adjustment
    if (finalPosition === 'top' || finalPosition === 'bottom') {
      left = triggerRect.left + scrollX + (triggerRect.width / 2) - (tooltipRect.width / 2);
      // Re-check horizontal bounds
      if (left < padding) {
        left = padding;
      } else if (left + tooltipRect.width > viewportWidth - padding) {
        left = viewportWidth - tooltipRect.width - padding;
      }
    }
    
    setActualPosition(finalPosition);
    return { top, left };
  }, [position]);

  const updatePosition = useCallback(() => {
    clearRaf();
    rafRef.current = requestAnimationFrame(() => {
      const newPosition = calculatePosition();
      if (newPosition) {
        setTooltipPosition(newPosition);
      }
    });
  }, [calculatePosition, clearRaf]);

  const showTooltip = useCallback(() => {
    isHoveringRef.current = true;
    clearShowTimeout();
    timeoutRef.current = setTimeout(() => {
      if (isHoveringRef.current) {
        setIsVisible(true);
      }
    }, delay);
  }, [delay, clearShowTimeout]);

  const hideTooltip = useCallback(() => {
    isHoveringRef.current = false;
    clearShowTimeout();
    setIsVisible(false);
  }, [clearShowTimeout]);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      setIsPositioned(false);
      // Use requestAnimationFrame to ensure tooltip is rendered and measured
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updatePosition();
          setIsPositioned(true);
        });
      });
      return () => cancelAnimationFrame(rafId);
    } else {
      setIsPositioned(false);
    }
  }, [isVisible, updatePosition]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isVisible) return;

    let ticking = false;
    const handleUpdate = () => {
      if (!ticking) {
        ticking = true;
        updatePosition();
        requestAnimationFrame(() => {
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', handleUpdate, { passive: true, capture: true });
    window.addEventListener('resize', handleUpdate, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleUpdate, { capture: true });
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isVisible, updatePosition]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearShowTimeout();
      clearRaf();
    };
  }, [clearShowTimeout, clearRaf]);

  // Clone child element with event handlers
  // Only show tooltips on hover for lg+ screens (1024px+)
  // Always show on focus for accessibility
  // In React 19 ref is a regular prop (children.props.ref), not children.ref
  const existingRef = (children.props as { ref?: React.Ref<HTMLElement> })?.ref;
  const childWithRef = React.cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      if (typeof existingRef === 'function') {
        existingRef(node);
      } else if (existingRef && typeof existingRef === 'object') {
        (existingRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    onMouseEnter: isLargeScreen ? (e: React.MouseEvent) => {
      showTooltip();
      children.props.onMouseEnter?.(e);
    } : children.props.onMouseEnter,
    onMouseLeave: isLargeScreen ? (e: React.MouseEvent) => {
      hideTooltip();
      children.props.onMouseLeave?.(e);
    } : children.props.onMouseLeave,
    onFocus: (e: React.FocusEvent) => {
      if (showOnFocus) showTooltip();
      if (children.props.onFocus) {
        children.props.onFocus(e);
      }
    },
    onBlur: (e: React.FocusEvent) => {
      if (showOnFocus) hideTooltip();
      if (children.props.onBlur) {
        children.props.onBlur(e);
      }
    },
  });

  const tooltipContent = isVisible ? (
    <div
      ref={tooltipRef}
      className="fixed z-[10000] pointer-events-none"
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        opacity: isPositioned ? 1 : 0,
        transition: isPositioned ? 'opacity 150ms ease-out' : 'none',
        pointerEvents: 'none',
      }}
      onMouseEnter={() => {
        // Keep tooltip visible if mouse moves to it
        isHoveringRef.current = true;
      }}
      onMouseLeave={() => {
        hideTooltip();
      }}
    >
      <div
        className={`
          px-3 py-2 rounded-lg text-sm font-medium text-white
          backdrop-blur-xl bg-gray-900/95 dark:bg-gray-800/95
          shadow-2xl border border-gray-700/50
          ${typeof content === 'string' ? 'whitespace-nowrap' : ''}
        `}
        style={{
          backdropFilter: 'blur(12px) saturate(180%)',
          WebkitBackdropFilter: 'blur(12px) saturate(180%)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3), 0 2px 8px 0 rgba(0, 0, 0, 0.2)',
        }}
      >
        {content}
        {/* Arrow */}
        <div
          className={`
            absolute w-2 h-2 bg-gray-900/95 dark:bg-gray-800/95 border-gray-700/50
            ${actualPosition === 'top' ? 'bottom-[-4px] left-1/2 -translate-x-1/2 border-b border-r rotate-45' : ''}
            ${actualPosition === 'bottom' ? 'top-[-4px] left-1/2 -translate-x-1/2 border-t border-l rotate-45' : ''}
            ${actualPosition === 'left' ? 'right-[-4px] top-1/2 -translate-y-1/2 border-b border-r rotate-45' : ''}
            ${actualPosition === 'right' ? 'left-[-4px] top-1/2 -translate-y-1/2 border-t border-l rotate-45' : ''}
          `}
        />
      </div>
    </div>
  ) : null;

  return (
    <>
      {childWithRef}
      {typeof document !== 'undefined' && createPortal(tooltipContent, document.body)}
    </>
  );
};

/**
 * Defers mounting the real Tooltip until after client hydration.
 * Use for content that is part of the initial static/SSR render (e.g. hit cards, lists).
 * Renders only the trigger child on server and first paint; tooltip behavior loads after mount.
 */
export const LazyTooltip: React.FC<TooltipProps> = (props) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) {
    return props.children;
  }
  return <Tooltip {...props}>{props.children}</Tooltip>;
};
